import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import '@/App.css';

// Components
import Navigation from './components/Navigation';
import AuthModal from './components/AuthModal';
import LoadingSpinner from './components/LoadingSpinner';
import OnboardingTour from './components/OnboardingTour';
import SkipLink from './components/SkipLink';

// Pages
import MapPage from './pages/MapPage';
import EventsPage from './pages/EventsPage';
import IEPHubPage from './pages/IEPHubPage';
import CommunityPage from './pages/CommunityPage';
import ProfilePage from './pages/ProfilePage';
import WelcomePage from './pages/WelcomePage';
import DisclaimerPage from './pages/DisclaimerPage';
import ContactPage from './pages/ContactPage';
import AccessibilityStatementPage from './pages/AccessibilityStatementPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';

const AppContent = () => {
  const { user, loading, isAuthenticated, demoMode } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [activeTab, setActiveTab] = useState('welcome');
  const [activePage, setActivePage] = useState(null); // For footer pages
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Check if first-time user and show onboarding
  // Demo mode users always see onboarding tour
  useEffect(() => {
    if (isAuthenticated) {
      if (demoMode) {
        // Always show onboarding for demo users
        setShowOnboarding(true);
      } else {
        // For real users, only show once
        const hasSeenOnboarding = localStorage.getItem('ablefam_onboarding_complete');
        if (!hasSeenOnboarding) {
          setShowOnboarding(true);
        }
      }
    }
  }, [isAuthenticated, demoMode]);

  const handleOnboardingComplete = () => {
    // Only save to localStorage for real users, not demo
    if (!demoMode) {
      localStorage.setItem('ablefam_onboarding_complete', 'true');
    }
    setShowOnboarding(false);
    setActiveTab('map');
  };

  const handleOnboardingSkip = () => {
    // Only save to localStorage for real users, not demo
    if (!demoMode) {
      localStorage.setItem('ablefam_onboarding_complete', 'true');
    }
    setShowOnboarding(false);
  };

  // Close modal when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && showAuthModal) {
      setShowAuthModal(false);
      // Don't auto-redirect to map, let onboarding handle it
    }
  }, [isAuthenticated, showAuthModal]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const handleGetStarted = () => {
    if (isAuthenticated) {
      setActiveTab('map');
    } else {
      setShowAuthModal(true);
    }
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    setActiveTab('map');
  };

  const handleBackFromFooterPage = () => {
    setActivePage(null);
  };

  // Render footer pages if active
  if (activePage === 'disclaimer') {
    return <DisclaimerPage onBack={handleBackFromFooterPage} />;
  }
  if (activePage === 'contact') {
    return <ContactPage onBack={handleBackFromFooterPage} />;
  }
  if (activePage === 'accessibility') {
    return <AccessibilityStatementPage onBack={handleBackFromFooterPage} />;
  }
  if (activePage === 'privacy') {
    return <PrivacyPolicyPage onBack={handleBackFromFooterPage} />;
  }
  if (activePage === 'terms') {
    return <TermsOfServicePage onBack={handleBackFromFooterPage} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Skip to main content link for accessibility */}
      <SkipLink targetId="main-content" />

      <div className="flex flex-col min-h-screen">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200" role="banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <button 
                onClick={() => {
                  setActiveTab('welcome');
                  setActivePage(null);
                }}
                className="flex items-center hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg"
                aria-label="Go to home page"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_content-creator-117/artifacts/tx7t9k76_3cc0bf35-2f98-49f3-85cf-7fba1af59ce1.png" 
                  alt="AbleFam accessibility logo" 
                  className="w-10 h-10 mr-3 object-contain"
                />
                <div className="flex flex-col text-left">
                  <h1 className="text-xl sm:text-2xl font-bold text-blue-600 leading-tight" data-testid="app-title">
                    AbleFam
                  </h1>
                  <span className="text-xs text-gray-500 leading-tight hidden sm:block">
                    Accessibility & Inclusion Hub
                  </span>
                </div>
              </button>
              
              {isAuthenticated && (
                <div className="hidden md:flex items-center space-x-4">
                  <span className="text-sm text-gray-700 truncate max-w-40" data-testid="user-name">
                    Welcome, {user?.name}
                  </span>
                  <div className="flex items-center space-x-1 whitespace-nowrap">
                    <span className="text-xs text-blue-600 font-medium">Score:</span>
                    <span className="text-sm font-bold text-blue-600" data-testid="contribution-score">
                      {user?.contribution_score || 0}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Navigation */}
        {isAuthenticated && (
          <Navigation activeTab={activeTab} setActiveTab={setActiveTab} />
        )}

        {/* Main Content */}
        <main id="main-content" className="flex-1" role="main">
          {!isAuthenticated ? (
            <WelcomePage onGetStarted={handleGetStarted} />
          ) : activeTab === 'welcome' ? (
            <WelcomePage onGetStarted={() => setActiveTab('map')} />
          ) : (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              {activeTab === 'map' && <MapPage />}
              {activeTab === 'events' && <EventsPage />}
              {activeTab === 'iep' && <IEPHubPage />}
              {activeTab === 'community' && <CommunityPage />}
              {activeTab === 'profile' && <ProfilePage />}
            </div>
          )}
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-gray-200 py-8" role="contentinfo">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Disclaimer notice */}
            <p className="text-center text-sm text-gray-600 mb-4">
              AbleFam provides informational and community-based resources and is not a substitute for professional advice.
            </p>
            
            <div className="text-center text-sm text-gray-500">
              <p>© 2025 AbleFam. Empowering families through accessibility and inclusion.</p>
              <div className="mt-3 flex flex-wrap justify-center gap-x-2 gap-y-1">
                <button 
                  onClick={() => setActivePage('privacy')}
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Privacy Policy
                </button>
                <span className="text-gray-400">•</span>
                <button 
                  onClick={() => setActivePage('terms')}
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Terms of Service
                </button>
                <span className="text-gray-400">•</span>
                <button 
                  onClick={() => setActivePage('disclaimer')}
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Legal Disclaimer
                </button>
                <span className="text-gray-400">•</span>
                <button 
                  onClick={() => setActivePage('accessibility')}
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Accessibility
                </button>
                <span className="text-gray-400">•</span>
                <button 
                  onClick={() => setActivePage('contact')}
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Contact Us
                </button>
              </div>
            </div>
          </div>
        </footer>
      </div>

      {/* Auth Modal */}
      {showAuthModal && !isAuthenticated && (
        <AuthModal 
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
        />
      )}

      {/* Onboarding Tour for First-Time Users */}
      {showOnboarding && isAuthenticated && (
        <OnboardingTour 
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
        />
      )}
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
